// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/MentorOperacional/PropostasPendentes.tsx
//  Fila de propostas de ESCRITA aguardando confirmação humana. São criadas por
//  canais sem UI (ex.: MCP — um assistente externo conectado ao ERP): nada é
//  gravado até um usuário confirmar aqui (o gate). Confirmar executa a ação pelo
//  caminho auditado; rejeitar descarta.
// ════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, Loader2, RefreshCw, Eye, X, Inbox, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentesService } from '@/features/agentes/services/agentesService';
import type { PropostaPendente } from '@/features/agentes/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

function prettyJson(raw: string): string {
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

function formatarData(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
}

export default function PropostasPendentes() {
  const [propostas, setPropostas] = useState<PropostaPendente[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [ocupada, setOcupada] = useState<number | null>(null); // id em ação (confirmar/rejeitar)
  const [verArgs, setVerArgs] = useState<Set<number>>(new Set());

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setPropostas(await agentesService.listarPropostasPendentes());
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao carregar as propostas pendentes.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  function toggleArgs(id: number) {
    setVerArgs((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });
  }

  async function confirmar(p: PropostaPendente) {
    setOcupada(p.id);
    try {
      const r = await agentesService.confirmarProposta(p.id);
      if (r.sucesso) toast.success(r.resumo || 'Ação executada com sucesso.');
      else toast.error(r.erro ?? r.resumo ?? 'A ação falhou.');
      await carregar();
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao confirmar a proposta.');
    } finally {
      setOcupada(null);
    }
  }

  async function rejeitar(p: PropostaPendente) {
    setOcupada(p.id);
    try {
      await agentesService.rejeitarProposta(p.id);
      toast.success('Proposta rejeitada.');
      await carregar();
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao rejeitar a proposta.');
    } finally {
      setOcupada(null);
    }
  }

  return (
    <Card className={propostas.length > 0 ? 'border-amber-300/60' : undefined}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-amber-600" />
          Propostas pendentes de confirmação
          {propostas.length > 0 && (
            <Badge variant="secondary" className="ml-1">{propostas.length}</Badge>
          )}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={() => void carregar()} disabled={carregando}
          title="Atualizar">
          <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Ações de escrita pedidas por assistentes conectados (ex.: MCP). Nada foi
          gravado — confirme para executar pelo caminho auditado, ou rejeite.
        </p>

        {carregando && propostas.length === 0 && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
          </div>
        )}

        {!carregando && propostas.length === 0 && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Inbox className="h-4 w-4" /> Nenhuma proposta pendente.
          </div>
        )}

        {propostas.map((p) => {
          const aberta = verArgs.has(p.id);
          const busy = ocupada === p.id;
          return (
            <div key={p.id} className="rounded-md border p-3">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{p.resumoHumano}</span>
                  <Badge variant="outline" className="text-[10px] uppercase">{p.origem}</Badge>
                  {p.permissaoNecessaria && (
                    <Badge variant="outline" className="text-[10px]">{p.permissaoNecessaria}</Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-mono">{p.ferramenta}</span>
                  <span>·</span>
                  <span>{formatarData(p.criadoEm)}</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-foreground"
                    onClick={() => toggleArgs(p.id)}
                  >
                    <Eye className="h-3 w-3" /> {aberta ? 'ocultar' : 'ver'} detalhes
                  </button>
                </div>
                {aberta && (
                  <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/80 p-2 text-[11px]">
                    {prettyJson(p.argumentosJson)}
                  </pre>
                )}
              </div>

              <Separator className="my-3" />

              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void rejeitar(p)}
                  disabled={busy}
                >
                  <X className="mr-1 h-4 w-4" /> Rejeitar
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" disabled={busy} className="bg-amber-600 hover:bg-amber-700">
                      {busy
                        ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        : <ShieldCheck className="mr-1 h-4 w-4" />}
                      Confirmar e executar
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        Confirmar a ação?
                      </AlertDialogTitle>
                      <AlertDialogDescription className="space-y-2">
                        <span className="block font-medium text-foreground">{p.resumoHumano}</span>
                        <span className="block">
                          Isto executa a ação de escrita de verdade (
                          <span className="font-mono text-xs">{p.ferramenta}</span>) pelo
                          caminho auditado. Não dá para desfazer automaticamente.
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => void confirmar(p)}
                      >
                        Confirmar e executar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
